import React, { useState, useEffect, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import moment from "moment";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  THEME_COLOR,
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";
import DatePicker from "../../../components/DatePicker";

import {
  deleteData,
  getAlert,
  getDataWithJoin,
  insertData,
  parseCurrency,
  updateData,
} from "../../../../utils/SamuiUtils";

const generateNumericPassword = () =>
  Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join("");

const PREFIX_OPTIONS = ["นาย", "นาง", "น.ส.", "อื่นๆ"];
// รองรับเบอร์โทรมือถือไทยที่ขึ้นต้นด้วย 06, 08, 09 (10 หลัก)
const THAI_MOBILE_PATTERN = /^(06|08|09)\d-\d{3}-\d{4}$/;

const formatThaiMobileNumber = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const EmployeeForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  dataEmpPosition,
  onRefreshPosition,
  dataEmpRole,
  onRefreshRole,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordToShow, setPasswordToShow] = useState("");
  const [showAddPositionModal, setShowAddPositionModal] = useState(false);
  const [newPositionName, setNewPositionName] = useState("");
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const prefixValue = formData.emp_prefix ?? "";
  const hasCustomPrefix =
    prefixValue !== "" && !PREFIX_OPTIONS.includes(prefixValue);

  const handleBack = () => {
    window.location.href = "/employee";
  };

  useEffect(() => {
    const compId = localStorage.getItem("company");
    if (compId) {
      setFormData((prevData) => {
        // Only update if comp_id is different to prevent unnecessary re-renders
        if (prevData.comp_id !== compId) {
          return {
            ...prevData,
            comp_id: compId,
          };
        }
        return prevData;
      });
    }
  }, []);

  const prevEmpIdRef = useRef(null);
  useEffect(() => {
    if (mode === "I") {
      const newPassword = generateNumericPassword();
      setGeneratedPassword(newPassword);
      setPasswordToShow("");
      const initialData = {
        emp_id: parseCurrency(window.localStorage.getItem("emp_id"), 10),
        emp_code: "",
        emp_prefix: "",
        emp_name: "",
        emp_position: "",
        emp_startdate: moment().format("YYYY-MM-DD"),
        emp_tel: "",
        emp_status: "Y",
        emp_uuser: "",
        emp_ppass: newPassword,
        emp_role: 3,
        comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
      };
      setFormData(initialData);
      prevEmpIdRef.current = initialData.emp_id;
    } else if (mode === "U" && selectedData) {
      const currentEmpId = parseCurrency(selectedData.Emp_Id, 10);
      // Only update if emp_id actually changed to prevent infinite loops
      if (prevEmpIdRef.current !== currentEmpId) {
        setGeneratedPassword("");
        setPasswordToShow("");
        const initialData = {
          emp_id: currentEmpId,
          emp_code: selectedData.Emp_Code,
          emp_prefix: selectedData.Emp_Prefix,
          emp_name: selectedData.Emp_Name,
          emp_position:
            selectedData.Emp_Position !== undefined && selectedData.Emp_Position !== null
              ? String(selectedData.Emp_Position)
              : "",
          emp_startdate: selectedData.Emp_StartDate,
          emp_tel: formatThaiMobileNumber(selectedData.Emp_Tel),
          emp_status: selectedData.Emp_Status,
          emp_uuser: selectedData.Emp_UUser,
          emp_ppass: selectedData.Emp_PPass,
          emp_role: parseCurrency(selectedData.Emp_Role, 10),
          comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
        };
        setFormData(initialData);
        prevEmpIdRef.current = currentEmpId;
      }
    }
  }, [mode, selectedData?.Emp_Id, setFormData, setGeneratedPassword, setPasswordToShow]);

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (event) => {
    const formattedValue = formatThaiMobileNumber(event.target.value);
    setFormData((prev) => ({
      ...prev,
      emp_tel: formattedValue,
    }));
  };

  const handleChangeDataEmpPosition = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      emp_position: value,
    }));
  };

  const handleDateChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      emp_startdate: value ? value.format("YYYY-MM-DD") : null,
    }));
  };

  const resetForm = useCallback(() => {
    setFormData({
      emp_id: "",
      emp_code: "",
      emp_prefix: "",
      emp_name: "",
      emp_position: "",
      emp_startdate: "",
      emp_tel: "",
      emp_status: "Y",
      emp_uuser: "",
      emp_ppass: "",
      emp_role: 3,
      comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
    });
    setGeneratedPassword("");
    setPasswordToShow("");
  }, []);

  // Move sanitizeEmployeePayload outside component to prevent recreation
  const sanitizeEmployeePayload = useCallback((data) => {
    const parseNullableInt = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      return parseCurrency(value, 10);
    };

    const normalizedStartDate =
      data.emp_startdate && data.emp_startdate !== ""
        ? moment(data.emp_startdate).format("YYYY-MM-DD")
        : null;

    const resolvedRole =
      data.emp_role === undefined ? 3 : data.emp_role;

    return {
      emp_code: data.emp_code?.trim() || null,
      emp_prefix: data.emp_prefix?.trim() || null,
      emp_name: data.emp_name?.trim() || null,
      emp_position: parseNullableInt(data.emp_position),
      emp_startdate: normalizedStartDate,
      emp_tel: data.emp_tel?.trim() || null,
      emp_status: (data.emp_status || "Y").toUpperCase(),
      emp_uuser: data.emp_uuser?.trim() || null,
      emp_ppass:
        data.emp_ppass === null || data.emp_ppass === undefined
          ? null
          : String(data.emp_ppass).trim(),
      emp_role: parseNullableInt(resolvedRole),
      comp_id: parseNullableInt(
        data.comp_id ?? window.localStorage.getItem("company")
      ),
    };
  }, []);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleInsertEmployee = useCallback(async () => {
    try {
      const currentFormData = formDataRef.current;
      const { emp_id, ...dataToInsert } = currentFormData;
      const sanitizedData = sanitizeEmployeePayload(dataToInsert);

      const fields = Object.keys(sanitizedData).join(", ");
      const values = Object.values(sanitizedData);

      await insertData("Tb_Set_Employee", fields, values);
      await callInitialize();
      return true;
    } catch (error) {
      console.error("Error inserting employee:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
      return false;
    }
  }, [callInitialize, sanitizeEmployeePayload]);

  const formatValues = useCallback((data) => sanitizeEmployeePayload(data), [sanitizeEmployeePayload]);

  const handleUpdateEmployee = useCallback(async () => {
    try {
      const currentFormData = formDataRef.current;
      const where = { emp_id: currentFormData.emp_id };
      const values = formatValues(currentFormData);
      const res = await updateData("Tb_Set_Employee", values, where);
      const rowsAffected = Array.isArray(res?.rowsAffected)
        ? res.rowsAffected[0]
        : 0;
      const isSuccess =
        res?.status === "OK" || (rowsAffected ? rowsAffected > 0 : false);
      if (isSuccess) {
        return true;
      } else {
        getAlert("FAILED", res?.message || "ไม่สามารถอัปเดตข้อมูลได้");
        return false;
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
      return false;
    }
  }, [formatValues]);

  const checkEmpCode = async () => {
    const resItemCode = await getDataWithJoin(
      `a.*`,
      `[dbo].[Tb_Set_Employee] AS a`,
      `WHERE a.Emp_Code = '${formData.emp_code}'`,
      ""
    );

    if (resItemCode && resItemCode.length > 0) {
      getAlert("FAILED", "มีรหัสพนักงานแล้ว");
      setFormData((prev) => ({ ...prev, emp_code: "" }));
    } else {
      getAlert("SUCCESS", "รหัสพนักงานนี้สามารถใช้ได้");
    }
  };

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.Emp_Id) {
          await deleteData(
            "Tb_Set_Employee",
            `WHERE Emp_Id = ${selectedData.Emp_Id}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const generatedPasswordRef = useRef(generatedPassword);
  useEffect(() => {
    generatedPasswordRef.current = generatedPassword;
  }, [generatedPassword]);

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const currentFormData = formDataRef.current;
      if (!currentFormData.emp_code || !currentFormData.emp_name || !currentFormData.emp_position) {
        getAlert("WARNING", "โปรดกรอกข้อมูลพนักงานให้ครบถ้วน");
        return;
      }

      if (!currentFormData.emp_uuser || !currentFormData.emp_ppass) {
        getAlert("WARNING", "โปรดกรอกข้อมูลบัญชีผู้ใช้งานให้ครบถ้วน");
        return;
      }

      // Validate phone number format only if it's not empty
      if (currentFormData.emp_tel && currentFormData.emp_tel.trim() !== "") {
        // Check if phone number has 10 digits (after removing non-digits)
        const digitsOnly = currentFormData.emp_tel.replace(/\D/g, "");
        if (digitsOnly.length !== 10) {
          getAlert("WARNING", "เบอร์โทรต้องมี 10 หลัก");
          return;
        }
        // Check format pattern - รองรับเบอร์โทรมือถือไทย (06x, 08x, 09x)
        if (!THAI_MOBILE_PATTERN.test(currentFormData.emp_tel)) {
          getAlert("WARNING", "โปรดกรอกเบอร์โทรให้เป็นรูปแบบ 06x-xxx-xxxx, 08x-xxx-xxxx หรือ 09x-xxx-xxxx");
          return;
        }
      }

      if (mode === "I") {
        const isSuccess = await handleInsertEmployee();
        if (isSuccess) {
          const passwordForModal =
            currentFormData.emp_ppass || generatedPasswordRef.current || "";
          setPasswordToShow(passwordForModal);
          setShowPasswordModal(true);
        }
      } else if (mode === "U") {
        const isSuccess = await handleUpdateEmployee();
        if (isSuccess) {
          await callInitialize();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      getAlert(
        "WARNING",
        "โปรดตรวจสอบข้อมูลอีกครั้ง ข้อมูลนี้มีอยู่ในระบบแล้ว"
      );
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [mode, handleInsertEmployee, handleUpdateEmployee, callInitialize, resetForm]);

  // Keyboard shortcuts - must be after handleSubmit is defined
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleCopyPassword = async () => {
    const valueToCopy = passwordToShow || generatedPassword;
    if (!valueToCopy) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(valueToCopy);
      } else {
        throw new Error("Clipboard API is not available");
      }
    } catch (error) {
      console.error("Error copying password:", error);
      getAlert("FAILED", "ไม่สามารถคัดลอกรหัสผ่านได้");
    }
  };

  const handleClosePasswordModal = async () => {
    setShowPasswordModal(false);
    resetForm();
    await callInitialize();
  };

  const handleAddPosition = async () => {
    if (!newPositionName || !newPositionName.trim()) {
      getAlert("WARNING", "โปรดกรอกชื่อตำแหน่ง");
      return;
    }

    try {
      const fields = "PST_Name, PST_Status";
      const values = [newPositionName.trim(), "Y"];
      await insertData("Tb_Set_Position", fields, values);
      
      getAlert("SUCCESS", "เพิ่มตำแหน่งสำเร็จ");
      
      // Refresh position data
      if (onRefreshPosition) {
        const updatedPositions = await onRefreshPosition();
        
        // Auto select the newly added position
        if (updatedPositions && updatedPositions.length > 0) {
          const newPosition = updatedPositions.find(p => p.PST_Name === newPositionName.trim());
          if (newPosition) {
            setFormData((prev) => ({
              ...prev,
              emp_position: String(newPosition.PST_Id),
            }));
          }
        }
      }
      
      setShowAddPositionModal(false);
      setNewPositionName("");
    } catch (error) {
      console.error("Error adding position:", error);
      getAlert("FAILED", "ไม่สามารถเพิ่มตำแหน่งได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleCloseAddPositionModal = () => {
    setShowAddPositionModal(false);
    setNewPositionName("");
  };

  const handleAddRole = async () => {
    if (!newRoleName || !newRoleName.trim()) {
      getAlert("WARNING", "โปรดกรอกชื่อบทบาท");
      return;
    }

    try {
      const fields = "Role_Name, Role_Status";
      const values = [newRoleName.trim(), "Y"];
      await insertData("Tb_Set_Role", fields, values);
      
      getAlert("SUCCESS", "เพิ่มบทบาทสำเร็จ");
      
      // Refresh role data
      if (onRefreshRole) {
        const updatedRoles = await onRefreshRole();
        
        // Auto select the newly added role
        if (updatedRoles && updatedRoles.length > 0) {
          const newRole = updatedRoles.find(r => r.Role_Name === newRoleName.trim());
          if (newRole) {
            setFormData((prev) => ({
              ...prev,
              emp_role: String(newRole.Role_Id),
            }));
          }
        }
      }
      
      setShowAddRoleModal(false);
      setNewRoleName("");
    } catch (error) {
      console.error("Error adding role:", error);
      getAlert("FAILED", "ไม่สามารถเพิ่มบทบาทได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleCloseAddRoleModal = () => {
    setShowAddRoleModal(false);
    setNewRoleName("");
  };

  return (
    <>
      <InitialDataFormLayout
        name={name}
        entityPath="/employee"
        mode={mode}
        onSubmit={handleSubmit}
        isSubmittingDisabled={isSaving}
        onBack={handleBack}
        onReset={resetForm}
        onDelete={mode === "U" ? handleDelete : undefined}
        callInitialize={callInitialize}
      >
        <div className="mb-5">
          <div style={sectionHeaderStyle}>
            <i className="fas fa-user-tie" style={sectionIconStyle}></i>
            <h5 style={sectionTitleStyle}>ข้อมูลพนักงาน</h5>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <label style={labelStyle}>รหัสพนักงาน</label>
              <input
                type="text"
                className="form-control"
                name="emp_code"
                value={formData.emp_code || ""}
                onBlur={checkEmpCode}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            {/* Hidden: คำนำหน้า field */}
            {/* <div className="col-md-4">
              <label style={labelStyle}>คำนำหน้า</label>
              <select
                className="form-select"
                name="emp_prefix"
                value={prefixValue}
                onChange={handleChange}
                style={selectStyle}
              >
                <option value="">-- เลือกคำนำหน้า --</option>
                {PREFIX_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
                {hasCustomPrefix && (
                  <option value={prefixValue}>{prefixValue}</option>
                )}
              </select>
            </div> */}
            <div className="col-md-4">
              <label style={labelStyle}>ชื่อ</label>
              <input
                type="text"
                className="form-control"
                name="emp_name"
                value={formData.emp_name || ""}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <label style={{ ...labelStyle, width: "50px", marginBottom: 0 }} className="mb-0">ตำแหน่ง</label>
                <button
                  type="button"
                  className="btn p-0"
                  onClick={() => setShowAddPositionModal(true)}
                  style={{
                    backgroundColor: "transparent",
                    color: THEME_COLOR,
                    border: "none",
                    padding: "2px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                  title="เพิ่มตำแหน่ง"
                >
                  <i className="fas fa-plus-circle"></i>
                </button>
              </div>
              <select
                className="form-select"
                name="emp_position"
                value={formData.emp_position ?? ""}
                onChange={handleChangeDataEmpPosition}
                style={selectStyle}
                disabled={dataEmpPosition.length <= 0}
              >
                <option value="">-- เลือกตำแหน่ง --</option>

                {formData.emp_position &&
                  !dataEmpPosition.some((t) => String(t.PST_Id) === String(formData.emp_position)) && (
                    <option value={String(formData.emp_position)}>
                      {selectedData?.PST_Name || "ตำแหน่งเดิม"}
                    </option>
                  )}

                {dataEmpPosition.map((type) => (
                  <option key={type.PST_Id} value={String(type.PST_Id)}>
                    {type.PST_Name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mb-5">
          <div style={sectionHeaderStyle}>
            <i className="fas fa-id-badge" style={sectionIconStyle}></i>
            <h5 style={sectionTitleStyle}>การติดต่อและเริ่มงาน</h5>
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <label style={labelStyle}>เบอร์โทร</label>
              <input
                type="tel"
                className="form-control"
                name="emp_tel"
                value={formData.emp_tel || ""}
                onChange={handlePhoneChange}
                style={inputStyle}
                placeholder="06x-xxx-xxxx, 08x-xxx-xxxx หรือ 09x-xxx-xxxx"
                inputMode="tel"
                pattern="^(06|08|09)\\d-\\d{3}-\\d{4}$"
                maxLength={12}
              />
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-2 mb-2">
                <label style={{ ...labelStyle, width: "50px", marginBottom: 0 }} className="mb-0">บทบาท</label>
                <button
                  type="button"
                  className="btn p-0"
                  onClick={() => setShowAddRoleModal(true)}
                  style={{
                    backgroundColor: "transparent",
                    color: THEME_COLOR,
                    border: "none",
                    padding: "2px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                  title="เพิ่มบทบาท"
                >
                  <i className="fas fa-plus-circle"></i>
                </button>
              </div>
              <select
                className="form-select"
                name="emp_role"
                value={formData.emp_role ?? ""}
                onChange={handleChange}
                style={selectStyle}
                disabled={dataEmpRole.length <= 0}
              >
                <option value="">-- เลือกบทบาท --</option>

                {formData.emp_role &&
                  !dataEmpRole.some((r) => String(r.Role_Id) === String(formData.emp_role)) && (
                    <option value={String(formData.emp_role)}>
                      {selectedData?.Role_Name || "บทบาทเดิม"}
                    </option>
                  )}

                {dataEmpRole.map((role) => (
                  <option key={role.Role_Id} value={String(role.Role_Id)}>
                    {role.Role_Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>วันที่เริ่มงาน</label>
              <DatePicker
                label=""
                name=""
                value={formData.emp_startdate}
                onChange={handleDateChange}
                disabled={false}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionHeaderStyle}>
            <i className="fas fa-lock" style={sectionIconStyle}></i>
            <h5 style={sectionTitleStyle}>บัญชีผู้ใช้งาน</h5>
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <label style={labelStyle}>บัญชีผู้ใช้</label>
              <input
                type="text"
                className="form-control"
                name="emp_uuser"
                value={formData.emp_uuser || ""}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>รหัสผ่าน</label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  name="emp_ppass"
                  value={formData.emp_ppass || ""}
                  onChange={handleChange}
                  style={inputStyle}
                />
                <button
                  type="button"
                  className="btn text-white"
                  onClick={toggleShowPassword}
                  style={{
                    backgroundColor: THEME_COLOR,
                    borderRadius: "0 8px 8px 0",
                  }}
                >
                  <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                </button>
              </div>
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>สถานะ</label>
              <select
                className="form-select"
                name="emp_status"
                value={formData.emp_status || "Y"}
                onChange={handleChange}
                style={selectStyle}
              >
                <option value="Y">ใช้งาน</option>
                <option value="N">ไม่ใช้งาน</option>
              </select>
            </div>
          </div>
        </div>
      </InitialDataFormLayout>

      {showPasswordModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content bg-white">
                <div className="modal-header">
                  <h5 className="modal-title">รหัสผ่านเริ่มต้น</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleClosePasswordModal}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <p className="mb-3">
                    รหัสผ่านสำหรับพนักงานใหม่ โปรดคัดลอกและส่งให้ผู้ใช้งาน
                  </p>
                  <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                    <span className="fs-4 fw-bold">
                      {passwordToShow || generatedPassword}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleCopyPassword}
                    >
                      คัดลอก
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-warning text-white"
                    onClick={handleClosePasswordModal}
                  >
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showAddPositionModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content bg-white">
                <div className="modal-header">
                  <h5 className="modal-title">เพิ่มตำแหน่งใหม่</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCloseAddPositionModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label style={labelStyle}>ชื่อตำแหน่ง</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newPositionName}
                      onChange={(e) => setNewPositionName(e.target.value)}
                      style={inputStyle}
                      placeholder="กรอกชื่อตำแหน่ง"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddPosition();
                        }
                      }}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseAddPositionModal}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    className="btn text-white"
                    onClick={handleAddPosition}
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    บันทึก
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showAddRoleModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content bg-white">
                <div className="modal-header">
                  <h5 className="modal-title">เพิ่มบทบาทใหม่</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCloseAddRoleModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label style={labelStyle}>ชื่อบทบาท</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      style={inputStyle}
                      placeholder="กรอกชื่อบทบาท"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddRole();
                        }
                      }}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseAddRoleModal}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    className="btn text-white"
                    onClick={handleAddRole}
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    บันทึก
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
};

export default EmployeeForm;
